import type { Build, SmokeTestProgress } from '@todesktop/shared';
import semver from 'semver';
import CliError from '../../../utilities/CliError';

const MIN_RUNTIME_VERSION = '1.2.3-0';

export function isBuildTestable(build: Partial<Build>): boolean {
  try {
    validateBuild(build);
    return true;
  } catch {
    return false;
  }
}

export function isTestRunning(build: Build): boolean {
  if (!build.smokeTest) {
    return false;
  }

  const { isFinished } = makeProgress(build);
  return !isFinished;
}

export const defaultVM = {
  cpu: '',
  agentVersion: '',
  image: '',
  imageVersion: '',
};

export function makeProgress(build: Build): CliTestProgress {
  const defaultProgress: OsTestProgress = {
    message: 'Starting',
    progress: 0,
    state: 'progress',
    shouldSkip: false,
    vm: defaultVM,
  };

  const linux: OsTestProgress = {
    ...defaultProgress,
    ...build.smokeTest?.linux,
    shouldSkip: build.linux?.shouldSkip || false,
  };
  const mac: OsTestProgress = {
    ...defaultProgress,
    ...build.smokeTest?.mac,
    shouldSkip: build.mac?.shouldSkip || false,
  };
  const windows: OsTestProgress = {
    ...defaultProgress,
    ...build.smokeTest?.windows,
    shouldSkip: build.windows?.shouldSkip || false,
  };

  const platforms: OsTestProgress[] = [linux, mac, windows].filter(
    (p) => !p.shouldSkip && p.state !== 'skipped',
  );

  const isCanceled = Boolean(build.smokeTest?.isCanceled);
  const isFinished =
    isCanceled || platforms.every((p) => p.state !== 'progress');

  let totalState: TotalTestProgress['state'] = 'progress';
  if (isCanceled) {
    totalState = 'canceled';
  } else if (platforms.every((p) => p.state === 'done')) {
    totalState = 'done';
  } else if (isFinished && platforms.some((p) => p.state === 'error')) {
    totalState = 'error';
  }

  return {
    isFinished,
    linux,
    mac,
    windows,
    total: {
      error: platforms
        .filter((p) => p.state === 'error')
        .map((p) => p.message)
        .join('\n\n'),
      state: totalState,
    },
  };
}

export function validateBuild(build: Partial<Build>) {
  if (build.status !== 'succeeded') {
    throw new CliError(
      `The build must be completed successfully. ` +
        `Actual build status: ${build.status}`,
    );
  }

  const actualRange =
    build.todesktopRuntimeVersionUsed || build.todesktopRuntimeVersionSpecified;

  if (!actualRange) {
    throw new CliError(
      'The build has no todesktopRuntimeVersionSpecified or ' +
        'todesktopRuntimeVersionUsed set. ' +
        'Please make sure @todesktop/runtime is installed as a dependency ' +
        'and make a new build',
    );
  }

  if (!isRuntimeVerRangeAllowed(actualRange)) {
    throw new CliError(
      `This build should have @todesktop/runtime version ${MIN_RUNTIME_VERSION} or later to run smoke tests.`,
    );
  }
}

function isRuntimeVerRangeAllowed(runtimeVerRange: string) {
  const minRuntimeVersionForApp = semver.minVersion(runtimeVerRange, {
    loose: true,
  });
  if (!minRuntimeVersionForApp) {
    return false;
  }

  return semver.gte(minRuntimeVersionForApp, MIN_RUNTIME_VERSION, {
    loose: true,
  });
}

export interface CliTestProgress {
  isFinished: boolean;
  linux: OsTestProgress;
  mac: OsTestProgress;
  windows: OsTestProgress;
  total: TotalTestProgress;
}

export interface OsTestProgress extends SmokeTestProgress {
  shouldSkip?: boolean;
}

export interface TotalTestProgress {
  error: string;
  state: 'progress' | 'done' | 'error' | 'canceled';
}
